import React, { useState, useMemo } from 'react';
import { useData } from 'src/contexts/DataContext';
import {
  PrinterTitle,
  PrinterThanksButton,
  PrinterCurrentPlan,
  PrinterPagesPerMonth,
  PrinterButton,
  PrinterCommitmentDetails,
  PrinterDescription,
  PrinterQuickLink,
  PrinterTotalPermonth,
  CheckmarkText,
  PrinterDivider,
  ActionContainer,
  PrinterMainTitle,
  OutOfStockInfoText,
  ToolTipText,
  StyledTooltip
} from './styles';
import {
  IconCheckmark,
  IconPrinterInkDrop,
  IconWarningAlt,
  IconInfo
} from '@veneer/core';
import AutomaticFirmwareUpdatesModal from '../../AutomaticFirmwareUpdatesModal';
import { PrinterItem } from 'src/utils/Products/productFilter';
import { useTranslationWithInterpolation } from 'src/hooks/useTranslationWithValues/useTranslationWithValuesHelper';
import DOMPurify from 'dompurify';
import {
  calculatePriceDifference,
  formatPrice
} from 'src/utils/Products/getCalculatePriceDifferance';
import SamePrinterModal from '../../SamePrinterModal';

interface RecommendedPrinterProps {
  nonCurrentPrinterDetails?: PrinterItem[];
  currentPrinterDetails?: PrinterItem[];
}

const RecommendedPrinterComponent = ({
  nonCurrentPrinterDetails,
  currentPrinterDetails
}: RecommendedPrinterProps): JSX.Element => {
  const dataContext = useData();
  const { selectedPrinterSku } = dataContext || {};
  const recommendedPrinter = nonCurrentPrinterDetails?.find(
    (p: PrinterItem) => p.recommendedPrinter === true
  );

  const recommendedPlan = recommendedPrinter?.inkPlan.find(
    (plan) => plan.recommendedPlan === true
  );
  const isSamePrinter =
    recommendedPrinter?.printerName === currentPrinterDetails?.[0]?.printerName;

  const { increaseDecreaseAmount, increaseOrDecrease } =
    calculatePriceDifference(
      recommendedPlan?.totalPrice,
      currentPrinterDetails[0]?.inkPlan[0].totalPrice
    );
  let planNote = '';
  const { getText, getTextWithValues } = useTranslationWithInterpolation();

  const [showFirmwareModal, setShowFirmwareModal] = useState(false);
  const [showSamePrinterModal, setShowSamePrinterModal] = useState(false);

  const planType = recommendedPrinter?.currentPrinter || isSamePrinter;
  const htmlString = recommendedPrinter?.shortDescription?.html || '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const showDescriptionText = doc.body.textContent || '';
  const isOutOfStock = useMemo(
    () => recommendedPrinter?.stockStatus !== 'IN_STOCK',
    [recommendedPrinter]
  );
  const currentPagesPerMonth =
    currentPrinterDetails?.[0]?.inkPlan[0]?.pagesPerMonth;
  const recommendedPagesPerMonth = recommendedPlan?.pagesPerMonth;

  if (planType) {
    planNote = DOMPurify.sanitize(
      getText(
        'printerDetails.currentPlanInfo',
        'This is your current print plan.'
      )
    );
  } else {
    planNote = DOMPurify.sanitize(
      getTextWithValues(
        'printerDetails.currentPlan',
        'Your current plan is {{printerModel}} printer with {{pages}} pages/month{{paper}}. <br/> <b>Plan fee will {{difference}} by only {{charges}}/month</b>',
        {
          printerModel: currentPrinterDetails[0]?.printerName,
          pages: currentPrinterDetails[0]?.inkPlan[0]?.pagesPerMonth,
          difference: increaseOrDecrease,
          charges: `$${increaseDecreaseAmount}`,
          paper: currentPrinterDetails[0]?.isPaperActive
            ? `+ ${getText('printerDetails.paper', 'paper')}`
            : ''
        }
      )
    );
  }

  const handleSelectPrinter = () => {
    if (planType) {
      setShowSamePrinterModal(true);
    } else {
      setShowFirmwareModal(true);
    }
  };

  const handleCloseFirmwareModal = () => {
    setShowFirmwareModal(false);
  };

  const handleProceedFirmware = () => {
    setShowFirmwareModal(false);
    if (selectedPrinterSku && recommendedPrinter) {
      selectedPrinterSku(recommendedPrinter.productSKU);
    }
  };
  const handleCloseModal = () => {
    setShowSamePrinterModal(false);
  };

  const handleProceed = () => {
    setShowSamePrinterModal(false);
    setShowFirmwareModal(true);
  };

  return (
    <div>
      <PrinterMainTitle>{recommendedPrinter?.modelPurpose}</PrinterMainTitle>
      <PrinterTitle>{recommendedPrinter?.printerName}</PrinterTitle>
      <PrinterDescription>{showDescriptionText}</PrinterDescription>
      <PrinterQuickLink
        href={recommendedPrinter?.fullspecification}
        target="_blank"
      >
        {getText('printerDetails.seeAllSpecs', 'See all specs')}
      </PrinterQuickLink>
      <PrinterDivider />
      <PrinterCommitmentDetails>
        {getTextWithValues(
          'printerDetails.commitmentDetails',
          '{{years}} years commitment',
          {
            years: recommendedPlan?.commitmentPeriod
          }
        )}
      </PrinterCommitmentDetails>
      <PrinterTotalPermonth>
        {getTextWithValues(
          'printerDetails.totalPerMonth',
          'Total {{amount}}/month',
          {
            amount: formatPrice(Math.abs(recommendedPlan?.totalPrice))
          }
        )}
      </PrinterTotalPermonth>
      <PrinterPagesPerMonth>
        <IconCheckmark size={20} />{' '}
        <CheckmarkText>
          {getTextWithValues(
            'printerDetails.includesPages',
            'Includes {{pages}} pages/month',
            {
              pages: recommendedPlan?.pagesPerMonth
            }
          )}
          {recommendedPrinter?.isPaperActive && (
            <span> + {getText('printerDetails.paper', 'paper')}</span>
          )}
          {currentPagesPerMonth !== recommendedPagesPerMonth && (
            <StyledTooltip
              content={
                <ToolTipText>
                  {getText(
                    'printerDetails.tooltip',
                    'Your current print plan is not available for this printer. We suggest the closest matching print plan.'
                  )}
                </ToolTipText>
              }
              placement="top"
            >
              <IconInfo
                size={20}
                color={'gray12'}
              />
            </StyledTooltip>
          )}
        </CheckmarkText>
      </PrinterPagesPerMonth>
      <PrinterCurrentPlan>
        <div>
          <IconPrinterInkDrop size={20} />
        </div>
        <div dangerouslySetInnerHTML={{ __html: planNote }}></div>
      </PrinterCurrentPlan>
      <ActionContainer>
        <PrinterButton
          onClick={handleSelectPrinter}
          appearance="primary"
          data-testid="printerSelectButton"
          disabled={isOutOfStock}
        >
          {getText('printerDetails.selectPrinter', 'Select this printer')}
        </PrinterButton>
        <PrinterThanksButton
          appearance="ghost"
          onClick={() => window.history.back()}
        >
          {getText('printerDetails.upgradeLater', 'Upgrade later')}
        </PrinterThanksButton>
      </ActionContainer>
      {isOutOfStock && (
        <OutOfStockInfoText>
          <IconWarningAlt
            size={24}
            filled
          />
          <div
            className="info__text"
            dangerouslySetInnerHTML={{
              __html: getText(
                'printerDetails.outOfStock',
                '<b>This printer is out of stock.</b> You can choose a different printer or check back later.'
              )
            }}
          />
        </OutOfStockInfoText>
      )}

      <SamePrinterModal
        show={showSamePrinterModal}
        onClose={handleCloseModal}
        onProceed={handleProceed}
      />

      {/* Automatic Firmware Updates Modal */}
      <AutomaticFirmwareUpdatesModal
        show={showFirmwareModal}
        onClose={handleCloseFirmwareModal}
        onProceed={handleProceedFirmware}
      />
    </div>
  );
};

export default RecommendedPrinterComponent;
